import React from 'react'
import ReactApexChart from "react-apexcharts";

import getChartColorsArray from "../../../../Components/Common/ChartsDynamicColor";
import { DealRangeData } from '../../../../Components/Reports/ReportsMain';
import NoDataPlaceholder from '../../../../Components/shared/NoDataPlaceholder';

/**
 * The BarChart function is a React component that renders a bar chart based on the provided data and
 * options.
 * @returns The BarChart component is being returned.
 */
const BarChart = ({ dataColors, resultSet, title, chartFor, reportsFilter, masterData }) => {
    const product = reportsFilter.product;
    
    /**
     * The function "numberWithCommas" takes a number as input and returns a string representation of
     * that number with commas separating every three digits.
     * @param x - The parameter "x" is a number that you want to format with commas.
     * @returns the input number with commas added as thousands separators.
     */
    function numberWithCommas(x) {
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    var chartStackedBarColors = getChartColorsArray(dataColors); 

    const dataSet = resultSet?.loadResponse?.results[0]?.data;
    
    let dataSetSource = '';
    let dataSetSourcePeriod = '';

    if (resultSet?.loadResponse?.results[0].query.dimensions?.length >= 3) {
        dataSetSource = resultSet?.loadResponse?.pivotQuery?.dimensions[resultSet?.loadResponse?.pivotQuery?.dimensions.length - 1];
        dataSetSourcePeriod = resultSet?.loadResponse?.pivotQuery?.dimensions[resultSet?.loadResponse?.pivotQuery?.dimensions.length - 2];
    } else {
        dataSetSource = resultSet?.loadResponse?.results[0].query ? resultSet?.loadResponse?.results[0].query.dimensions[1] : resultSet?.loadResponse?.results[0].pivotQuery.dimensions[1];
        dataSetSourcePeriod = resultSet?.loadResponse?.results[0].query ? resultSet?.loadResponse?.results[0].query.dimensions[0] : resultSet?.loadResponse?.results[0].pivotQuery.dimensions[0];
    }

    /* Extracting unique industries from the resultSet */
    const dataSetSources = [...new Set(dataSet.map((data) => data[dataSetSource]))];

    let filterMeasure = '';
console.log(product,"oiuoy")
    if (product == 'PE' || product == 'VC') {
        if (chartFor == 'amount') {
            filterMeasure = 'DealFlat.AmountM';
        } else {
            filterMeasure = 'DealFlat.deal_id';
        }
    } else if (product == 'PE-Exits' || product == 'VC-Exits') {
        if (chartFor == 'amount') {
            filterMeasure = 'exit_investment_flat.sum_of_amount';
        } else {
            filterMeasure = 'exit_investment_flat.count';
        }
    } else if (product == 'MA') {
        if (chartFor == 'amount') {
            filterMeasure = 'MaInvestmentsFlat.ma_dealAmount';
        } else {
            filterMeasure = 'MaInvestmentsFlat.ma_dealCount';
        }
    } else {
        if (chartFor == 'amount') {
            filterMeasure = 'REinvestments.amount';
        } else {
            filterMeasure = 'REinvestments.count';
        }
    }
    
    const years = Array.from(new Set(dataSet.map(item => item[dataSetSourcePeriod])))
        .filter(year => year !== null)
        .map(year => String(year));

    const isMultipleDimension = resultSet?.loadResponse?.results[0].query.dimensions?.length >= 3;

    /* The `series` variable is an array that is created by mapping over the `dataSetSources` array.
    For each element in `dataSetSources`, it creates an object with two properties: `name` and
    `data`. */
    const series = dataSetSources.map((industry) => ({
        name: reportsFilter?.filterViewOption === "dealRange" ? DealRangeData.find(data => data.id === industry)?.name ?? industry : industry,
        data: years.map(year => {
            const key = isMultipleDimension 
                ? resultSet?.loadResponse?.results[0].query.dimensions[1]
                : resultSet?.loadResponse?.results[0].query.dimensions[0];

            const factor = isMultipleDimension 
                ? resultSet?.loadResponse?.results[0].query.dimensions[2]
                : resultSet?.loadResponse?.results[0].query.dimensions[1];

            const value = dataSet?.find(data => data[key] == year && data[factor] == industry);
            return value ? value[filterMeasure] : null;
        }) 
    }));
   
    // Calculate dynamic chart height based on number of years
    // Minimum height is 350px, and we add 25px per year after 5 years
    const baseHeight = 350;
    const extraHeightPerYear = 25;
    const minYearsForBaseHeight = 5;
    
    // Calculate the chart height dynamically
    const chartHeight = years.length <= minYearsForBaseHeight 
        ? baseHeight 
        : baseHeight + (years.length - minYearsForBaseHeight) * extraHeightPerYear;
    
    // Calculate dynamic bar height based on number of years
    // Base height of 15 works well for few years, but we need to adjust for many years
    const barHeight = Math.max(Math.min(65, 500 / years.length), 8);

    var options = {
        chart: {
            type: 'bar',
            height: chartHeight,
            stacked: true,
            toolbar: {
                show: false,
            }
        },
        dataLabels: {
            enabled: false,
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: barHeight,
                columnWidth: '70%',
            },
        },
        stroke: {
            width: 1,
            colors: ['#fff']
        },
        title: {
            text: '',
            style: {
                fontWeight: 500,
            },
        },
        xaxis: {
            categories: years,
            labels: {
                formatter: (value) => { return numberWithCommas(value); }
            },
            axisBorder: {
                show: true,
            },
            axisTicks: {
                show: true,
            },
        },
        yaxis: {
            title: {
                text: undefined
            },
            labels: {
                offsetY: years.length === 1 ? -10 : 0,
                style: {
                    fontSize: '12px',
                },
                formatter: (value, timeStampe, opts) => {
                    const dateMasterKeys = {
                        PE: "DateMasterCombo",
                        VC: "DateMasterCombo",
                        MA: "DateMasterCombo",
                        "PE-RE": "DateMasterCombo",
                        "PE-Exits": "DateMasterCombo",
                        "VC-Exits": "DateMasterCombo",
                    }
                    const periodKeys = {
                        CY: "cy",
                        FY: "fy",
                    }
                    const periodTypeKeys = {
                        quarterly: "qy",
                        halfYearly: "hy",
                        monthly: "my",
                        yearly: "",
                    }
                    if (reportsFilter?.period == "CY" && reportsFilter?.viewOption === "yearly") {
                        return value;
                    }
                    const searchKey = `${dateMasterKeys[product ?? "PE"]}.${periodKeys[reportsFilter?.period ?? "FY"]}${periodTypeKeys[reportsFilter?.viewOption ?? "yearly"]}`;
                    return masterData?.filter(data => data[searchKey] == value)?.at(0)?.[`${searchKey}Desc`] 
                }
            }
        },
        tooltip: {
            y: {
                formatter: function (val) {
                    return numberWithCommas(val);
                }
            }
        },
        fill: {
            opacity: 1
        },
        legend: {
            position: 'top',
            horizontalAlign: 'left',
            offsetX: 40
        },
        grid: {
            xaxis: {
                lines: {
                    show: true
                }
            },
            yaxis: {
                lines: {
                    show: false
                }
            }
        },
        colors: chartStackedBarColors,
    };

    return (
        <React.Fragment>
            <div className='bg-white border shadow rounded block w-full border-gray-100 md:ml-3 md:mr-3'>
                <h3 className='m-3 border-b-2 border-b-primaryColor w-fit'>{title}</h3>
                <div className='p-1'>
                    {dataSet.length > 0 ?
                        <ReactApexChart
                            className="apex-charts"
                            options={options}
                            series={series}
                            type="bar"
                            height={chartHeight}
                        />                        
                        : <div className='my-3'><NoDataPlaceholder showButton={false} /></div>
                    }
                </div>
            </div>
        </React.Fragment>
    );
};

export {
    BarChart
};